// One RFQ (Lane 1335): header, the account panel, the main table and each
// line's detail, as in the mockup. The account is the quoter's click, never
// automatic (Ryan, 2026-09-27); until then the customer columns say so.
// Changing a confirmed account clears the picks, so it takes a second click.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import QuotingLineDetail from './QuotingLineDetail';
import {
  customerLabel, gapText, headerFlagText, jobText, money, moneyWhole, pct, perUnit, qty, qtyCellText,
  sharedTiers, shortDate, tiersText,
} from './quotingText';

// One set of column widths for the header table and every line's table.
// "Price given" shows only on an RFQ whose file already prices some items
// (Lane 1399: Groves, where an account manager priced 7 of 10 rows).
const mainCols = (given) => (<colgroup><col style={{ width: 30 }} /><col style={{ width: 130 }} /><col style={{ width: 92 }} /><col /><col style={{ width: 84 }} />{given && <col style={{ width: 80 }} />}<col style={{ width: 78 }} /><col style={{ width: 96 }} /><col style={{ width: 88 }} /><col style={{ width: 88 }} /><col style={{ width: 80 }} /><col style={{ width: 70 }} /></colgroup>);

// Column names, shown once at the top and repeated (visually hidden) in each
// line's table so screen readers still read each cell's column (Codex 1347).
const mainHeads = (given) => ['Line', 'Customer part #', 'XMEK part #', 'Description', 'Qty',
  ...(given ? ['Price given'] : []), 'XMEK stock', 'Vendor lead time', 'XMEK cost', 'Last price', 'Last sold', 'Margin'];

const MARGIN_CLASS = (m) => (m == null ? '' : m < 15 ? 'qtng-bad' : m < 30 ? 'qtng-mid' : 'qtng-ok');

function stockCell(facts) {
  const stock = facts?.stock;
  if (!stock || stock.state === 'unknown') return <span className="qtng-mut" title="Could not load">?</span>;
  if (stock.state === 'no_file') return <span className="qtng-mut">No file</span>;
  const title = `Inventory file of ${shortDate(stock.as_of)}`;
  if (stock.state === 'not_in_file') return <span className="qtng-mut" title={title}>Not in file</span>;
  return <span className={stock.old ? 'qtng-old' : ''} title={title}>{qty(stock.on_hand)}</span>;
}

function vendorCell(facts) {
  const v = facts?.vendor;
  if (!v || v.state === 'unknown') return <span className="qtng-mut" title="Could not load">?</span>;
  if (v.state === 'no_file') return <span className="qtng-mut">No file</span>;
  if (v.state !== 'found') return '–';
  const which = v.basis === 'primary_vendor' ? 'primary vendor' : v.basis === 'shortest_lead_time' ? 'shortest lead time' : 'only vendor listed';
  const title = `${v.vendor_name || `Vendor ${v.vendor_code}`} (${which}), as of ${shortDate(v.lead_time_as_of)}${v.backorder ? `; backorder ${qty(v.available)}` : ''}`;
  const cls = v.backorder ? 'qtng-bad' : v.lead_time_old || v.available_old ? 'qtng-mid' : '';
  const text = v.lead_time == null ? '–' : `${qty(v.lead_time)}${v.lead_time_unit ? ` ${v.lead_time_unit}` : ''}`;
  return <span className={cls} title={title}>{text}</span>;
}

function costCell(facts) {
  const c = facts?.xmek_cost;
  if (!c || c.state === 'unknown') return <span className="qtng-mut" title="Could not load">?</span>;
  if (c.state !== 'found') return '–';
  const po = c.basis === 'last_paid_po';
  const paid = facts?.last_paid || {};
  const title = po ? `Last price paid on a PO, ${shortDate(c.date)} (no sale with a cost)` : `Invoice cost of the last sale, ${shortDate(c.date)}`;
  return <span className={c.far_from_last_paid ? 'qtng-mid' : ''} title={title}>
    {po ? money(c.unit_cost, paid.currency) : money(c.unit_cost)}{po ? `${perUnit(paid.uom)} PO` : ''}</span>;
}

function partCell(line) {
  if (line.ranking_out_of_date && !line.picked) return <span className="qtng-mut">Re-checking</span>;
  if (!line.selected) return <span className="qtng-mut">{line.candidates.length || (line.suggested || []).length ? 'Pick one' : '–'}</span>;
  if (line.picked) return <span title="You picked this part">{line.selected}</span>;
  if (line.needs_confirm) return <span className="qtng-fill" title="Wingman recommended this part by size and material. Open the line to confirm it.">{line.selected} · confirm</span>;
  if (line.match_basis?.fill_in === 'listed_by_customer') {
    const check = (line.flags || []).some((f) => f.startsWith('fill_in_check_'));
    return <span className={check ? 'qtng-fill' : ''} title="Listed in the customer's file">{line.selected}</span>;
  }
  const checks = (line.flags || []).some((f) => f.startsWith('fill_in_check_'));
  return <span className={checks ? 'qtng-fill' : ''} title={checks ? 'Filled in by Wingman. Open the line to see what to check.' : 'Filled in by Wingman from sales history'}>{line.selected}</span>;
}

// The confirmed accounts in one line: one account as before; several with
// one name "NAME · N accounts (codes)"; several names "NAME and N more
// accounts (codes)", NAME being the one with the most sales (Ryan, R1).
function accountsText(rfq) {
  const list = rfq.accounts || [];
  const name = rfq.customer_name || rfq.customer_code;
  const codes = list.map((a) => a.customer_code).join(', ');
  if (list.length <= 1) return <>Customer account: <b>{name}</b> ({rfq.customer_code})</>;
  if (list.every((a) => a.customer_name === list[0].customer_name)) {
    return <>Customer: <b>{name}</b> · {list.length} accounts ({codes})</>;
  }
  return <>Customer: <b>{name}</b> and {list.length - 1} more {list.length === 2 ? 'account' : 'accounts'} ({codes})</>;
}

// The customer's accounts (Lane 1357, Ryan's mockup 2026-09-30): tick one or
// several; "Select all N named X" ticks every account with that name, beyond
// the visible page too. Wingman never groups accounts on its own. The picker
// keeps the account revision it opened with: if another quoter changes the
// accounts meanwhile, it closes and says so, and never overwrites them.
function AccountPanel({ api, rfq, onChanged }) {
  const confirmed = rfq.customer_state === 'resolved';
  const current = (rfq.accounts || []).map((a) => a.customer_code).sort();
  const [open, setOpen] = useState(!confirmed);
  const [openedAt, setOpenedAt] = useState(rfq.account_revision || 0);
  const [q, setQ] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [ticked, setTicked] = useState(() => new Map());
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const start = () => {
    setOpenedAt(rfq.account_revision || 0);
    setTicked(new Map((rfq.accounts || []).map((a) => [a.customer_code, a])));
    setNotice(''); setError(''); setAsking(false); setOpen(true);
  };
  // Another quoter changed the accounts while this picker was open.
  useEffect(() => {
    if (open && confirmed && (rfq.account_revision || 0) !== openedAt) {
      setOpen(false); setAsking(false);
      setNotice('Another quoter changed the accounts. Check them before changing again.');
    }
  }, [open, confirmed, rfq.account_revision, openedAt]);

  // Only the latest search, and "Show more" of that same search, is shown:
  // a slow answer never mixes into a newer search (Codex 1357).
  const asked = useRef(0);
  const search = useCallback((text) => {
    const mine = ++asked.current;
    api.accounts(rfq.rfq_id, text === null ? undefined : text, 1)
      .then((r) => {
        if (mine !== asked.current) return;
        setResult(r); if (text === null) setQ(r.search || ''); setError('');
      })
      .catch((e) => { if (mine === asked.current) setError(e.message); });
  }, [api, rfq.rfq_id]);
  useEffect(() => { if (open && !result) search(null); }, [open, result, search]);

  const more = async () => {
    const mine = asked.current;
    setLoadingMore(true);
    try {
      const next = await api.accounts(rfq.rfq_id, result.search, result.next_page);
      if (mine !== asked.current) return;
      setResult((r) => ({ ...next, items: [...r.items, ...next.items], same_name: { ...r.same_name, ...next.same_name } }));
    } catch (e) { if (mine === asked.current) setError(e.message); } finally { setLoadingMore(false); }
  };

  const toggle = (a) => setTicked((t) => {
    const next = new Map(t);
    if (next.has(a.customer_code)) next.delete(a.customer_code); else next.set(a.customer_code, a);
    return next;
  });
  const tickAll = (name, codes) => setTicked((t) => {
    const next = new Map(t);
    codes.forEach((c) => { if (!next.has(c)) next.set(c, { customer_code: c, customer_name: name }); });
    return next;
  });
  const codes = [...ticked.keys()].sort();
  const same = codes.length === current.length && codes.every((c, i) => c === current[i]);
  // The same accounts again re-checks lines not yet checked for them (after
  // a failed check); it never clears a pick (Codex 1357).
  const unchecked = (rfq.lines || []).some((l) => l.current
    && (l.ranking_out_of_date || (l.flags || []).includes('matching_failed')));

  const save = async (chosen = codes) => {
    setSaving(true); setError('');
    try {
      await api.setAccounts(rfq.rfq_id, chosen, openedAt);
      setAsking(false); setOpen(false); setNotice('');
      await onChanged();
    } catch (e) {
      if (e.code === 'account_changed') {
        setOpen(false); setAsking(false);
        setNotice('Another quoter changed the accounts. Check them before changing again.');
        await onChanged();
      } else setError(e.message);
    } finally { setSaving(false); }
  };
  const use = () => { if (confirmed && !same) setAsking(true); else save(); };
  // One click: every account the search found, as one customer (Ryan,
  // 2026-09-30). Changing an already confirmed set still asks first.
  const useAll = () => {
    const all = result?.all || [];
    setTicked(new Map(all.map((a) => [a.customer_code, a])));
    const allCodes = all.map((a) => a.customer_code).sort();
    const sameAll = allCodes.length === current.length && allCodes.every((c, i) => c === current[i]);
    if (confirmed && !sameAll) setAsking(true); else save(allCodes);
  };

  if (rfq.customer_state === 'not_applicable') return null;
  const sameName = Object.entries(result?.same_name || {});
  const listed = new Set((result?.items || []).map((a) => a.customer_code));
  const offPage = [...ticked.values()].filter((a) => !listed.has(a.customer_code));
  return (
    <div className="qtng-acct">
      {notice && <div className="qtng-err" role="status">{notice}</div>}
      {confirmed && !open ? (
        <div className="qtng-acctrow">
          <span>{accountsText(rfq)}</span>
          <button type="button" className="qtng-link" onClick={start}>Change</button>
        </div>
      ) : (
        <>
          <div className="qtng-accth">{confirmed ? 'Change the customer accounts' : 'Confirm the customer account'}
            <span className="qtng-mut" style={{ fontWeight: 400 }}>{result?.search_source === 'sender_address' && rfq.customer_hint?.domain
              ? (rfq.customer_name_as_written
                ? ` · no account matches "${rfq.customer_name_as_written}"; searching by the sender's email address (${rfq.customer_hint.domain})`
                : ` · the email writes no company name; searching by the sender's email address (${rfq.customer_hint.domain})`)
              : rfq.customer_name_as_written
                ? ` · the email says "${rfq.customer_name_as_written}"`
                : ' · the email writes no company name'}</span></div>
          <form className="qtng-acctrow" onSubmit={(e) => { e.preventDefault(); search(q || ''); }}>
            <input className="qtng-input" value={q ?? ''} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts by name or code" aria-label="Search customer accounts" />
            <button type="submit" className="qtng-btn">Search</button>
            {result?.all && <button type="button" className="qtng-btn qtng-btn--primary" disabled={saving} onClick={useAll}>Use all {result.all.length} accounts</button>}
            {result && !result.all && result.total > 50 && <span className="qtng-mut qtng-small">{result.total} accounts found; search narrower to use them together (50 at most).</span>}
            {sameName.map(([name, all]) => (
              <button key={name} type="button" className="qtng-link" onClick={() => tickAll(name, all)}>Select all {all.length} named {name}</button>
            ))}
            {confirmed && <button type="button" className="qtng-link" onClick={() => { setOpen(false); setAsking(false); }}>Cancel</button>}
          </form>
          {error && <div className="qtng-err">{error}</div>}
          {result?.status === 'not_imported' && <div className="qtng-empty">No sales file in Wingman, so there are no accounts to choose from.</div>}
          {result && result.status !== 'not_imported' && !(result.items || []).length && <div className="qtng-empty">No account matches "{result.search}". Try part of the name.</div>}
          {((result?.items || []).length > 0 || offPage.length > 0) && (
            <div className="qtng-scroll" style={{ marginTop: 8 }}>
              <table className="qtng-t">
                <thead><tr><th style={{ width: 32 }}><span className="qtng-sr">Use</span></th><th>Account</th><th>Code</th><th className="qtng-r">Sales</th><th className="qtng-r">Orders</th><th className="qtng-r">Last order</th></tr></thead>
                <tbody>
                  {/* Ticked accounts not in this search stay in view (and ticked). */}
                  {offPage.map((a) => (
                    <tr key={`t-${a.customer_code}`} className="qtng-this">
                      <td><input type="checkbox" checked onChange={() => toggle(a)} aria-label={`Use ${a.customer_code}`} /></td>
                      <td>{a.customer_name}</td><td className="qtng-num">{a.customer_code}</td><td /><td /><td />
                    </tr>
                  ))}
                  {(result?.items || []).map((a) => (
                    <tr key={a.customer_code} className={ticked.has(a.customer_code) ? 'qtng-this' : ''}>
                      <td><input type="checkbox" checked={ticked.has(a.customer_code)} onChange={() => toggle(a)} aria-label={`Use ${a.customer_code}`} /></td>
                      <td>{a.customer_name}</td><td className="qtng-num">{a.customer_code}</td>
                      <td className="qtng-r qtng-num">{moneyWhole(a.screened_net_sales)}</td>
                      <td className="qtng-r qtng-num">{qty(a.orders)}</td>
                      <td className="qtng-r qtng-num">{shortDate(a.last_invoice_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result?.next_page && (
            <div className="qtng-acctrow" style={{ marginTop: 6 }}>
              <span className="qtng-mut qtng-small">Showing {result.items.length} of {result.total} accounts.</span>
              <button type="button" className="qtng-link" disabled={loadingMore} onClick={more}>{loadingMore ? 'Loading…' : 'Show more accounts'}</button>
            </div>
          )}
          <div className="qtng-acctrow" style={{ marginTop: 10 }}>
            <button type="button" className="qtng-btn qtng-btn--primary" disabled={saving || !codes.length || (confirmed && same && !unchecked)} onClick={use}>
              {confirmed && same ? 'Check the lines again'
                : codes.length > 1 ? `Use these ${codes.length} accounts together` : 'Use this account'}</button>
            {!codes.length && <span className="qtng-mut qtng-small">Tick one account, or several to quote them as one customer.</span>}
          </div>
          {asking && (
            <div className="qtng-confirm">
              Change the accounts to {codes.length === 1 ? <b>{ticked.get(codes[0]).customer_name} ({codes[0]})</b> : <b>{codes.length} accounts ({codes.join(', ')})</b>}? The parts you picked on this RFQ are cleared, and Wingman re-checks every line for these accounts.
              <div className="qtng-acctrow" style={{ marginTop: 8 }}>
                <button type="button" className="qtng-btn qtng-btn--primary" disabled={saving} onClick={() => save()}>{saving ? 'Saving…' : 'Yes, change them'}</button>
                <button type="button" className="qtng-btn" onClick={() => setAsking(false)}>Keep the current accounts</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// The reader's notes and the stored files, one short line that opens on
// click (Ryan, 2026-09-27: the summary was too long to read at a glance).
function HeaderMore({ rfq, openFile }) {
  const [show, setShow] = useState('');
  const notes = rfq.requirements || [];
  const files = (rfq.attachments || []).filter((a) => a.stored);
  if (!notes.length && !files.length) return null;
  const flip = (which) => setShow((s) => (s === which ? '' : which));
  return (
    <>
      <div className="qtng-more-row">
        {notes.length > 0 && <button type="button" className="qtng-link" aria-expanded={show === 'notes'} onClick={() => flip('notes')}>
          {notes.length} {notes.length === 1 ? 'note' : 'notes'} from the email</button>}
        {files.length > 0 && <button type="button" className="qtng-link" aria-expanded={show === 'files'} onClick={() => flip('files')}>
          {files.length} {files.length === 1 ? 'attachment' : 'attachments'}</button>}
      </div>
      {show === 'notes' && <ul className="qtng-notes">{notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
      {show === 'files' && (
        <div className="qtng-files">
          {files.map((a) => (
            <button key={a.rfq_attachment_id} type="button" className="qtng-link" onClick={() => openFile({ attachment: a })}>{a.original_filename || (String(a.detected_content_type || '').startsWith('text/') ? 'Email text' : 'Unnamed file')}</button>
          ))}
        </div>
      )}
    </>
  );
}

// The column names stay at the top of the window while the lines scroll, and
// the open line's row sticks just under them (Ryan, 2026-09-27: "we still
// need the column headers"). The header's height is measured, so the row's
// offset follows it when the header wraps.
function useHeaderHeight() {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setHeight(el.offsetHeight));
    ro.observe(el);
    setHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);
  return [ref, height];
}

export default function QuotingRfq({ api, rfq, reload, openFile }) {
  const [open, setOpen] = useState(null);
  const [headRef, headHeight] = useHeaderHeight();
  const confirmed = rfq.customer_state === 'resolved';
  const who = confirmed ? (rfq.customer_name || rfq.customer_code) : 'this customer';
  const lines = rfq.lines || [];
  // The same price breaks on every line: said once, in plain view (Lane 1349).
  const shared = sharedTiers(lines.filter((l) => l.current));
  const title = `${customerLabel(rfq) || 'Customer not named'}${rfq.customer_rfq_number ? ` · RFQ ${rfq.customer_rfq_number}` : ''}`;
  const current = lines.filter((l) => l.current);
  const old = lines.filter((l) => !l.current);
  const state = jobText(rfq.job, rfq.intake_enabled);
  const busy = ['queued', 'waiting', 'reading', 'updating'].includes(rfq.job?.state);
  const given = current.some((l) => l.given?.given_price);
  const MAIN_COLS = mainCols(given);
  const MAIN_HEADS = mainHeads(given);
  const qtyAt = 4;
  const missed = rfq.rows_not_on_lines || [];
  const missedTotal = rfq.rows_not_on_lines_total || missed.length;
  const noLine = rfq.tables_with_no_line || [];
  const notCounted = rfq.sheets_not_counted || [];
  const tableName = (t) => (t.sheet ? `sheet "${t.sheet}" of ${t.filename || 'the spreadsheet'}` : `a table in ${t.filename || 'the email'}`);
  const attachmentOf = (partPath) => (rfq.attachments || []).find((a) => a.part_path === partPath && a.stored);

  const lineRow = (l) => {
    const c = l.facts?.customer;
    const isOpen = open === l.line_id;
    // Each line is its own table with the same fixed columns, so an open
    // line's row can stick to the top of the window while its detail scrolls
    // under it, and lets go when the detail ends (Ryan, 2026-09-27: like a
    // frozen row in Excel). A sticky cell is held by its whole table, so one
    // shared table would keep it over the next lines (measured, Lane 1347).
    return (
      <table key={l.line_id} className="qtng-main qtng-linetab">{MAIN_COLS}
        <thead className="qtng-sr"><tr>{MAIN_HEADS.map((h, i) => <th key={i} scope="col">{h}</th>)}</tr></thead>
        <tbody className={isOpen ? 'qtng-opengrp' : undefined}>
        <tr className={`qtng-row ${isOpen ? 'qtng-open' : ''} ${l.current ? '' : 'qtng-old-line'}`} onClick={() => setOpen(isOpen ? null : l.line_id)}>
          <td className="qtng-mut">{l.line_number}</td>
          <td className="qtng-num qtng-cut" title={l.cpn || ''}>{l.cpn || '–'}</td>
          <td className="qtng-num">{partCell(l)}</td>
          <td className="qtng-pn qtng-cut" title={l.description || ''}>{l.description || '–'}</td>
          <td className="qtng-r qtng-num" title={(l.quantity_scenarios || []).length > 1 ? `Tiers: ${tiersText(l.quantity_scenarios)}` : undefined}>{qtyCellText(l.quantity_scenarios)}</td>
          {given && <td className="qtng-r qtng-num" title={l.given?.given_price ? "Price already written in the customer's file" : undefined}>{l.given?.given_price?.value || '–'}</td>}
          <td className="qtng-r qtng-num">{l.selected ? stockCell(l.facts) : '–'}</td>
          <td className="qtng-r qtng-num">{l.selected ? vendorCell(l.facts) : '–'}</td>
          <td className="qtng-r qtng-num">{l.selected ? costCell(l.facts) : '–'}</td>
          {!confirmed ? (
            <td className="qtng-r qtng-mut" colSpan={3}>Confirm account</td>
          ) : !l.selected ? (<><td className="qtng-r">–</td><td className="qtng-r">–</td><td className="qtng-r">–</td></>)
            : c?.state === 'found' ? (
              <>
                <td className="qtng-r qtng-num">{money(c.last_price)}</td>
                <td className="qtng-r qtng-num">{shortDate(c.last_sold)}
                  {/* Several accounts: which one bought (mockup step 2). */}
                  {(rfq.accounts || []).length > 1 && c.bought_by
                    ? <span className="qtng-acctag" title={`Bought by account ${c.bought_by}`}>{c.bought_by}</span> : null}</td>
                <td className={`qtng-r qtng-num ${MARGIN_CLASS(c.margin_pct)}`}>{pct(c.margin_pct)}</td>
              </>
            ) : c?.state === 'never_sold' ? (
              <><td className="qtng-r">–</td><td className="qtng-r qtng-mut">never</td><td className="qtng-r">–</td></>
            ) : (<td className="qtng-r qtng-mut" colSpan={3} title="Could not load">?</td>)}
        </tr>
        {isOpen && (
          <tr className="qtng-det"><td colSpan={MAIN_HEADS.length}>
            <QuotingLineDetail api={api} rfq={rfq} line={l} who={who} onPicked={reload} openFile={openFile} />
          </td></tr>
        )}
      </tbody></table>
    );
  };

  return (
    <div className="qtng-box qtng-rfqbox">
      <div className="qtng-rh">
        <h2>{title}</h2>
        <div className="qtng-rhsub">{[rfq.subject, rfq.sender ? `forwarded by ${rfq.sender}` : null].filter(Boolean).join(' · ')}</div>
        <div className="qtng-facts">
          <div><span>Received</span>{shortDate(rfq.received_at)}</div>
          <div><span>Reply due</span>{rfq.reply_due_as_written || shortDate(rfq.reply_due_date)}</div>
          {rfq.completeness === 'partial' && <div className="qtng-old">Not everything could be read</div>}
          {missedTotal > 0 && <div className="qtng-old">{missedTotal === 1 ? '1 row in the files is not on any line' : `${missedTotal} rows in the files are not on any line`}</div>}
        </div>
        {shared && <div className="qtng-tiers"><span>Price breaks asked for</span>{shared}</div>}
        {state && busy && <div className="qtng-state">{state}. This page updates by itself.</div>}
        {rfq.job?.state === 'failed' && <div className="qtng-err">{state}.</div>}
        {rfq.document_kind === 'not_rfq' && <div className="qtng-state">Wingman read this as not a request for quote{rfq.not_rfq_reason ? `: ${rfq.not_rfq_reason}` : ''}.</div>}
        <HeaderMore rfq={rfq} openFile={openFile} />
      </div>
      {rfq.published && <AccountPanel api={api} rfq={rfq} onChanged={reload} />}
      {!rfq.published ? (
        <div className="qtng-empty">{state || 'Not read yet.'}</div>
      ) : (
        <div className="qtng-mainwrap" style={{ '--qtng-headh': `${headHeight}px` }}>
          <table className="qtng-main qtng-headtab" ref={headRef}>
            {MAIN_COLS}
            <thead>
              {/* Which RFQ this is, frozen with the column names (Ryan, 2026-09-28). */}
              <tr><th className="qtng-grp qtng-headtitle" colSpan={MAIN_HEADS.length - 3}>{title}</th><th className="qtng-grp qtng-gh" colSpan={3}>{confirmed ? who : 'Customer'}</th></tr>
              <tr>{MAIN_HEADS.map((h, i) => <th key={i} className={i >= qtyAt ? 'qtng-r' : undefined}>{i === 0 ? <span className="qtng-sr">{h}</span> : h}</th>)}</tr>
            </thead>
          </table>
          {current.map(lineRow)}
          {old.length > 0 && <div className="qtng-mut qtng-small" style={{ padding: '10px 8px', borderBottom: '1px solid var(--ll-hair)' }}>Picks on lines the latest read no longer found:</div>}
          {old.map(lineRow)}
        </div>
      )}
      {rfq.published && (
        <div className="qtng-gaps">
          Click a line to see its stock, costs and full buy and sell history. Orange = check it (old file, or a part Wingman filled in with something to check).
          {((rfq.gaps || []).length > 0 || (rfq.header_flags || []).length > 0 || (rfq.skipped_cad || []).length > 0 || missed.length > 0 || noLine.length > 0 || notCounted.length > 0) && (
            <ul>
              {missed.map((m) => {
                const where = m.sheet ? `Row ${m.row} of ${m.filename || 'the spreadsheet'}` : `Row ${m.row} of a table in ${m.filename || 'the email'}`;
                const a = attachmentOf(m.part_path);
                return (
                  <li key={m.id}>{`${where} (${m.value}) is not on any line. `}
                    {a && <button type="button" className="qtng-link" onClick={() => openFile({ attachment: a, highlight: [m.sheet ? { sheet: m.sheet, row: m.row } : m.value] })}>Show it</button>}
                  </li>
                );
              })}
              {missedTotal > missed.length && <li>{`And ${missedTotal - missed.length} more rows not on any line.`}</li>}
              {noLine.map((t, i) => <li key={`n${i}`}>{`No line comes from ${tableName(t)} (${t.rows} ${t.rows === 1 ? 'row' : 'rows'} with a part number). Check whether it lists items to quote.`}</li>)}
              {notCounted.map((t, i) => <li key={`c${i}`}>{`Wingman could not count the rows of ${tableName(t)}: it has no part number column heading.`}</li>)}
              {(rfq.gaps || []).map((g, i) => <li key={`g${i}`}>{gapText(g)}</li>)}
              {(rfq.skipped_cad || []).length > 0 && <li>{`Not read, as CAD drawing files hold no text to quote from: ${rfq.skipped_cad.map((f) => f.filename).join(', ')}.`}</li>}
              {(rfq.header_flags || []).map((f) => <li key={f}>{headerFlagText(f)}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
